import { EAxiosResponseCode, ESystemType, getHttpErrorMessage, Http } from '@core/api';
import { getHostbaseUrl } from '@core/tools';
import {
  EContractCreateStatus,
  EContractType,
  EEnable,
  EFillStatus,
  ETempBusiness,
  ETempStatus,
  IContractActorDropDownVO,
  IContractActorListVO,
  IContractCreateDropDownActorParams,
  IContractCreateOperateParams,
  IContractCreatePage,
  IContractCreatePageParams,
  IContractCreateParams,
  IContractDetailParams,
  IContractDetailVO,
  IContractDropDownVO,
  IContractFieldSysValueVO,
  IContractFreeAuthItem,
  IContractFreeAuthParams,
  IContractFreeAuthTempItem,
  IContractPlateInfoParams,
  IContractSignPage,
  IContractSignPageParams,
  IContractSysValueDTO,
  IContractTempActorFiledParams,
  IContractTempAuthItem,
  IContractTempAuthParams,
  IContractTempDO,
  IContractTempInfo,
  IContractTempParams,
  IFillRuleDTOParams,
  IFillV1RuleParams,
  IFreeWorkContractInfo,
  IFreeWorkContractParams,
  IPageResultContractTempVO,
  IServerContractTempGetTempParams,
  IServerContractTempItem,
  IServerContractTempParams,
  ITempActorFiledVO,
  ITempPlatFormVO,
} from '../../types/contracts/contracts';

const hostUrl = getHostbaseUrl();

/**
 * 合同中心 - 合同模版管理 - 模版分页列表
 */
export async function queryContractTempPage(param: IContractTempParams) {
  let data = [] as IPageResultContractTempVO[];
  let total = 0;

  param = JSON.parse(JSON.stringify(param)) as IContractTempParams;

  try {
    const { code, context, message } = await Http.post<{ data: IPageResultContractTempVO[]; total: number }>(`${hostUrl}/contract/contract-temp/page`, param);

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IPageResultContractTempVO[]; total: number };

      let newItems = items.map((item) => {
        return {
          ...item,
          isShowOpen: item.enable == EEnable.UNENABLE && item.tempStatus == ETempStatus.UPLOADED,
          isShowClose: item.enable == EEnable.ENABLE && item.tempStatus == ETempStatus.UPLOADED,
          isShowFill: item.fillStatus == EFillStatus.NOT_CONFIGURED && item.tempStatus == ETempStatus.UPLOADED,
          isShowDetail: item.fillStatus == EFillStatus.CONFIGURED && item.tempStatus == ETempStatus.UPLOADED,
        };
      });

      data = newItems || [] as IPageResultContractTempVO[];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 合同中心 - 合同模版管理 - 新增模版(返回跳转链接)
 */
export async function queryContractTempAdd() {
  let res = '';

  try {
    const { code, context, message } = await Http.post<string>(
      `${hostUrl}/contract/contract-temp/add`, {},
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同模版管理 - 导出管理列表
 */
export async function contractTempExport(params: IContractTempParams) {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.get<Blob>(
      `${hostUrl}/contract/contract-temp/export`,
      params,
      { responseType: 'blob' });

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 * 合同中心 - 合同模版管理 - 启用-关闭
 */
export async function queryContractTempEnable(params: { enable: number; id: number }) {
  let res = '';

  try {
    const { code, context, message } = await Http.get<string>(
      `${hostUrl}/contract/contract-temp/enable`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同模版管理 - 编辑(返回跳转链接)
 */
export async function queryContractTempEdit(id: number) {
  let res = '';

  try {
    const { code, context, message } = await Http.get<string>(
      `${hostUrl}/contract/contract-temp/edit`, { id },
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同模版管理 - 详情
 */
export async function queryContractTempDetail(id: number) {
  let res = {} as IContractTempInfo;

  try {
    const { code, context, message } = await Http.get<IContractTempInfo>(
      `${hostUrl}/contract/contract-temp/detail`, { id },
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || {} as IContractTempInfo;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同模版管理 - 下拉端信息(点击获取)
 */
export async function queryContractTempPlatformInfo(params: IContractPlateInfoParams) {
  let res = [] as ITempPlatFormVO[];

  try {
    const { code, context, message } = await Http.get<ITempPlatFormVO[]>(
      `${hostUrl}/contract/contract-temp/platform-info`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || [] as ITempPlatFormVO[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同模版管理 - 匹配合同字段信息(在下拉选中具体端后调用)
 */
export async function queryContractTempFieldInfo(params: IContractTempActorFiledParams) {
  let res = {} as ITempActorFiledVO;

  try {
    const { code, context, message } = await Http.get<ITempActorFiledVO>(
      `${hostUrl}/contract/contract-temp/field-info`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || {} as ITempActorFiledVO;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同模版管理 - 规则填充
 */
export async function queryContractTempFillRule(params: IFillRuleDTOParams) {
  let res = '';

  try {
    const { code, context, message } = await Http.post<string>(
      `${hostUrl}/contract/contract-temp/fill-rule`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同模版管理 - 合同模版预览地址获取
 */
export async function queryContractTempPreviewTempUrl(contractTempId: number) {
  let res = '';

  try {
    const { code, context, message } = await Http.get<string>(
      `${hostUrl}/contract/contract-temp/preview-temp-url`, { contractTempId },
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同模版管理 - 规则填充众包业务合同前置校验
 */
export async function queryContractTempPreCheck(id: number, businessType: ETempBusiness) {
  let res = '';

  try {
    const { code, context, message } = await Http.get<string>(
      `${hostUrl}/contract/contract-temp/crowd-sourcing/pre-check`, {
        id,
        businessType,
      },
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同模版管理 - 合同模版数据list(所有模版)
 */
export async function queryContractTempList(tempName?: string) {
  let res = [] as IContractTempDO[];

  try {
    const { code, context, message } = await Http.get<IContractTempDO[]>(
      `${hostUrl}/contract/contract-temp/list`, { tempName },
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || [] as IContractTempDO[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同模版管理 - 查询指定可用的合同模版
 */
export async function queryContractTempAvailableTemp(businessType: ETempBusiness[]) {
  let res = [] as IContractTempDO[];

  try {
    const { code, context, message } = await Http.post<IContractTempDO[]>(
      `${hostUrl}/contract/contract-temp/available/temp`, { businessType },
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || [] as IContractTempDO[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同模版管理 - 模版删除
 */
export async function deleteContractTemp(id: number) {
  let res = {};

  try {
    const { code, context, message } = await Http.get<{}>(
      `${hostUrl}/contract/contract-temp/temp-del`, { id },
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同模版管理 - 上传模版
 */
export async function uploadContractTemp(params: { tempName: string; tempUrl: string }) {
  let res = {};

  try {
    const { code, context, message } = await Http.post<any>(
      `${hostUrl}/contract/contract-temp/upload`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同模版管理 - 查询特代可授权的自由合同模版
 */
export async function queryContractTempAuthContractTemp(id: number) {
  let res = [] as IContractTempDO[];

  try {
    const { code, context, message } = await Http.get<IContractTempDO[]>(
      `${hostUrl}/contract/contract-temp/auth-contract-temp`, { id },
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || [] as IContractTempDO[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同模版管理 - V1规则填充
 */
export async function queryContractTempFillV1Rule(params: IFillV1RuleParams) {
  let res = '';

  try {
    const { code, context, message } = await Http.post<string>(
      `${hostUrl}/contract/contract-temp/fill-v1-rule`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同创建管理 - 分页
 */
export async function queryContractCreateMypage(params: IContractCreatePageParams) {
  let data = [] as IContractCreatePage[];
  let total = 0;

  params = JSON.parse(JSON.stringify(params)) as IContractCreatePageParams;

  try {
    const { code, context, message } = await Http.post<{ data: IContractCreatePage[]; total: number }>(`${hostUrl}/contract/contract-create/my-page`, params);

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IContractCreatePage[]; total: number };

      let newItem = items.map((item) => {
        return {
          ...item,
          showEdit: [EContractCreateStatus.REVOKED].includes(item?.status) && item?.contractType == EContractType.FREE_CONTRACT,
          showExtension: [
            EContractCreateStatus.TO_BE_SIGNED,
            EContractCreateStatus.SIGNING,
          ].includes(item?.status) && item?.contractType == EContractType.FREE_CONTRACT,
          showRevoke: [
            EContractCreateStatus.TO_BE_SIGNED,
            EContractCreateStatus.SIGNING,
          ].includes(item?.status) && item?.contractType != EContractType.OFFLINE_CONTRACT,
          showInvalidate: item?.status == EContractCreateStatus.COMPLETED && item?.contractType != EContractType.INVALID_CONTRACT,
        };
      });

      data = newItem || [] as IContractCreatePage[];

      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 合同中心 - 合同创建管理 - 发起方-参与方下拉数据
 */
export async function queryContractCreateDrapDown() {
  let res = {} as IContractDropDownVO;

  try {
    const { code, context, message } = await Http.get<IContractDropDownVO>(
      `${hostUrl}/contract/contract-create/drop-down`, {},
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || {} as IContractDropDownVO;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同创建管理 - 导出管理列表
 */
export async function contractCreateExport(params: IContractCreatePageParams) {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.get<Blob>(
      `${hostUrl}/contract/contract-create/export`,
      params,
      { responseType: 'blob' });

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 * 合同中心 - 合同创建管理 - 下载(返回下载地址url)
 */
export async function queryContractCreateDownload(params: { page: number; size: number; ids?: number[] }) {
  let res = '';

  try {
    const { code, context, message } = await Http.post<string>(
      `${hostUrl}/contract/contract-create/download`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同创建管理 - 合同预览地址获取
 */
export async function queryContractCreatePreview(id: number) {
  let res = '';

  try {
    const { code, context, message } = await Http.post<string>(
      `${hostUrl}/contract/contract-create/preview`, { id },
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同创建管理 - 作废-延期-撤销-撤销作废
 */
export async function queryContractCreateOperate(params: IContractCreateOperateParams) {
  let res = '';

  try {
    const { code, context, message } = await Http.get<string>(
      `${hostUrl}/contract/contract-create/operate`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同创建管理 - 合同延期剩余次数
 */
export async function queryContractCreateExtensionsNum(contractId: number) {
  let res = 0;

  try {
    const { code, context, message } = await Http.get<number>(
      `${hostUrl}/contract/contract-create/extensions-num`, { contractId },
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同创建管理 - 合同预览地址获取
 */
export async function queryContractCreate(params: IContractCreateParams) {
  let res = '';

  try {
    const { code, context, message } = await Http.post<string>(
      `${hostUrl}/contract/contract-create/create`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同创建管理 - 自由合同编辑
 */
export async function queryFreeContractUpdata(params: IContractCreateParams) {
  let res = '';

  try {
    const { code, context, message } = await Http.post<string>(
      `${hostUrl}/contract/contract-create/edit`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同创建管理 - 合同模版数据下拉
 */
export async function queryContractCreateDropDownTemp() {
  let res = [] as IContractTempDO[];

  try {
    const { code, context, message } = await Http.get<IContractTempDO[]>(
      `${hostUrl}/contract/contract-create/drop-down-temp`, {},
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || [] as IContractTempDO[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同创建管理 - 合同模版预览地址获取
 */
export async function queryContractCreatePreviewTempUrl(contractTempId: number) {
  let res = '';

  try {
    const { code, context, message } = await Http.get<string>(
      `${hostUrl}/contract/contract-create/preview-temp-url`, { contractTempId },
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同创建管理 - 合同详情
 */
export async function queryContractCreateDetail(params: IContractDetailParams) {
  let res = {} as IContractDetailVO;

  try {
    const { code, context, message } = await Http.get<IContractDetailVO>(
      `${hostUrl}/contract/contract-create/detail`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || {} as IContractDetailVO;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同创建管理 - 签署方查看
 */
export async function queryContractCreateActorInfo(id: number) {
  let res = [] as IContractActorListVO[];

  try {
    const { code, context, message } = await Http.get<IContractActorListVO[]>(
      `${hostUrl}/contract/contract-create/actor-info`, { id },
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || [] as IContractActorListVO[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同创建管理 - 合同模版数据下拉
 */
export async function queryContractCreateDropDownActor(params: IContractCreateDropDownActorParams) {
  let res = [] as IContractActorDropDownVO[];

  try {
    const { code, context, message } = await Http.get<IContractActorDropDownVO[]>(
      `${hostUrl}/contract/contract-create/drop-down-actor`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || [] as IContractActorDropDownVO[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同创建管理 - 系统字段值获取
 */
export async function queryContractCreateFindSysValue(params: IContractSysValueDTO) {
  let res = [] as IContractFieldSysValueVO[];

  try {
    const { code, context, message } = await Http.post<IContractFieldSysValueVO[]>(
      `${hostUrl}/contract/contract-create/find-sys/value`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || [] as IContractFieldSysValueVO[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同签署管理 - 合同列表分页(签署列表)
 */
export async function queryContractSignPage(params: IContractSignPageParams) {
  let data = [] as IContractSignPage[];
  let total = 0;

  params = JSON.parse(JSON.stringify(params)) as IContractSignPageParams;

  try {
    const { code, context, message } = await Http.post<{ data: IContractSignPage[]; total: number }>(`${hostUrl}/contract/contract-sign/sign-page`, params);

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IContractSignPage[]; total: number };

      data = items || [] as IContractSignPage[];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 合同中心 - 合同签署管理 - 导出管理列表
 */
export async function contractSignExport(params: IContractSignPageParams) {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.get<Blob>(
      `${hostUrl}/contract/contract-sign/export`,
      params,
      { responseType: 'blob' });

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 * 合同中心 - 合同签署管理 - 下载(返回下载地址url)
 */
export async function queryContractSignDownload(params: { page: number; size: number; ids?: number[] }) {
  let res = '';

  try {
    const { code, context, message } = await Http.post<string>(
      `${hostUrl}/contract/contract-sign/download`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同签署管理 - 合同预览地址获取
 */
export async function queryContractSignPreview(id: number) {
  let res = '';

  try {
    const { code, context, message } = await Http.post<string>(
      `${hostUrl}/contract/contract-sign/preview`, { id },
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同签署管理 - 获取签署方签署链接
 */
export async function queryContractSignActorSign(id: number) {
  let res = '';

  try {
    const { code, context, message } = await Http.post<string>(
      `${hostUrl}/contract/contract-sign/actor-sign`, { id },
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同签署管理 - 待签署数量
 */
export async function queryContractSignToSignCount() {
  let res = 0;

  try {
    const { code, context, message } = await Http.get<number>(
      `${hostUrl}/contract/contract-sign/to-signed/count`, {},
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同模版授权 - 列表
 */
export async function queryContractTempAuth() {
  let res = [] as IContractTempAuthItem[];

  try {
    const { code, context, message } = await Http.get<IContractTempAuthItem[]>(
      `${hostUrl}/contract/contract-temp-auth/list`, {},
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || [] as IContractTempAuthItem[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同模版授权 - 授权回显
 */
export async function queryContractTempAuthDetail(id: number) {
  let res = {} as IContractTempAuthItem;

  try {
    const { code, context, message } = await Http.get<IContractTempAuthItem>(
      `${hostUrl}/contract/contract-temp-auth/detail`, { id },
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || {} as IContractTempAuthItem;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同模版授权 - 授权
 */
export async function authContractTempAuth(params: IContractTempAuthParams) {
  let res = {};

  try {
    const { code, context, message } = await Http.post<any>(
      `${hostUrl}/contract/contract-temp-auth/auth`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同签署管理 - 自雇者协议-代征服务合同信息
 */
export async function queryFreeWorkContract(params: IFreeWorkContractParams) {
  let res = [] as IFreeWorkContractInfo[];

  try {
    const { code, context, message } = await Http.post<IFreeWorkContractInfo[]>(
      `${hostUrl}/contract/contract-sign/free-work-contract`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || [] as IFreeWorkContractInfo[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 合同签署管理 - 参与方下拉数据(合同列表签署页面筛选条件)
 */
export async function queryContractSignDrapDown() {
  let res = {} as IContractDropDownVO;

  try {
    const { code, context, message } = await Http.get<IContractDropDownVO>(
      `${hostUrl}/contract/contract-sign/drop-down-list`, {},
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || {} as IContractDropDownVO;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 服务商合同管理 - 列表
 */
export async function queryServerContractTemp(params: IServerContractTempParams) {
  let data = [] as IServerContractTempItem[];
  let total = 0;

  params = JSON.parse(JSON.stringify(params)) as IServerContractTempParams;

  try {
    const { code, context, message } = await Http.get<{ data: IServerContractTempItem[]; total: number }>(`${hostUrl}/contract/server-contract-temp/page`, params);

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IServerContractTempItem[]; total: number };

      data = items || [] as IServerContractTempItem[];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 合同中心 - 服务商合同管理 - 导出
 */
export async function exportServerContractTemp(params: IServerContractTempParams) {
  let buffer = null as Blob | null;
  let fileName = ''; // 默认文件名

  try {
    const { code, context, message, headers } = await Http.get<Blob>(
      `${hostUrl}/contract/server-contract-temp/export`,
      params,
      { responseType: 'blob' },
    );

    if (headers['content-disposition']) {
      const match = headers['content-disposition'].match(/filename=(.+);?/);

      if (match && match[1]) {
        fileName = decodeURIComponent(match[1].trim().replace(/"/g, ''));
      }
    }

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    buffer,
    fileName,
  };
}

/**
 * 合同中心 - 服务商合同管理 - 启用默认模版
 */
export async function queryServerContractTempEnable(id: number) {
  try {
    const { code, context, message } = await Http.get<{}>(
      `${hostUrl}/contract/server-contract-temp/enable-default-temp`, { id },
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 合同中心 - 服务商合同管理 - 配置模版
 */
export async function queryServerContractTempGetTemp(params: IServerContractTempGetTempParams) {
  try {
    const { code, context, message } = await Http.post<IContractTempAuthItem[]>(
      `${hostUrl}/contract/server-contract-temp/get-temp`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 合同中心 - 授权合同 - 自由合同授权管理 - 分页
 */
export async function queryContractFreeAuthPage(params: IContractFreeAuthParams) {
  let data = [] as IContractFreeAuthItem[];
  let total = 0;

  params = JSON.parse(JSON.stringify(params)) as IContractFreeAuthParams;

  try {
    const { code, context, message } = await Http.post<{ data: IContractFreeAuthItem[]; total: number }>(`${hostUrl}/contract/contract-free-auth/page`, params);

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IContractFreeAuthItem[]; total: number };

      data = items || [] as IContractFreeAuthItem[];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 合同中心 - 授权合同 - 自由合同授权管理 - 启停授权
 */
export async function enableContractFreeAuth(params: { id: number; status: number }) {
  let res = {};

  try {
    const { code, context, message } = await Http.get<any>(
      `${hostUrl}/contract/contract-free-auth/enable`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 授权合同 - 自由合同授权管理 - 删除
 */
export async function deleteContractFreeAuth(id: number) {
  let res = {};

  try {
    const { code, context, message } = await Http.get<any>(
      `${hostUrl}/contract/contract-free-auth/del`, { id },
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 授权合同 - 自由合同授权管理 - 启停授权
 */
export async function addContractFreeAuth(params: { platformId: number; contractTempId: number }) {
  let res = {};

  try {
    const { code, context, message } = await Http.post<any>(
      `${hostUrl}/contract/contract-free-auth/add`, params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 授权合同 - 自由合同授权管理 - 查询可授权特代列表
 */
export async function queryTopAgentContractFreeAuth(sysType?: ESystemType) {
  let res = [] as IContractFreeAuthTempItem[];

  try {
    const { code, context, message } = await Http.post<IContractFreeAuthTempItem[]>(
      `${hostUrl}/contract/contract-free-auth/temp-top-agent/list`, { sysType },
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || [] as IContractFreeAuthTempItem[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 合同中心 - 授权合同 - 自由合同授权管理 - 查询当前特代已授权的自由合同模版
 */
export async function queryContractFreeAuthTopAgentContractTemp() {
  let res = [] as IContractTempDO[];

  try {
    const { code, context, message } = await Http.post<IContractTempDO[]>(
      `${hostUrl}/contract/contract-free-auth/top-agent-contract-temp`, null,
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || [] as IContractTempDO[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

